import React, { useEffect, useMemo, useState } from "react";
import { fetchJson } from "../../lib/api";
import type {
  BookingResponse,
  ConsumptionResponse,
  MeterReadingsResponse,
  MeterReadingType,
} from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import {
  channelLabel,
  deviationDkkClass,
  deviationPercentClass,
  formatDkkValue,
  formatMeterValue,
  formatSignedDkk,
  formatSignedPercent,
  formatUsageValue,
} from "../../lib/format-utils";
import { UNAVAILABLE_STATUSES } from "../../../lib/booking-status";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { addDays, formatDate, parseUtcDate } from "../../lib/date-utils";
import { UtilityTypeTabs } from "../utility-type-tabs";
import { Button } from "../button";

const EXCLUDED_METER_READING_STATUSES = new Set(["own-use", "season-blocked"]);
const RED_DEVIATION_DKK_THRESHOLD = 300;
const toBookingAnchorId = (key: string): string => `booking-${key.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

export const MeterReadingsView = ({ houseId }: { houseId: number | null }) => {
  const { t } = useI18n();
  const [data, setData] = useState<MeterReadingsResponse | null>(null);
  const [consumptionData, setConsumptionData] = useState<ConsumptionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<MeterReadingType>("electricity");
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getUTCFullYear());
  const [isComplaintModalOpen, setIsComplaintModalOpen] = useState(false);
  const [complaintChannel, setComplaintChannel] = useState<"villavilla" | "admiralstrand">(
    "admiralstrand",
  );
  const [complaintStartDate, setComplaintStartDate] = useState("");
  const [complaintEndDate, setComplaintEndDate] = useState("");
  const [complaintThresholdDkk, setComplaintThresholdDkk] = useState(RED_DEVIATION_DKK_THRESHOLD);
  const [includeCleaningReadingColumn, setIncludeCleaningReadingColumn] = useState(true);
  const [complaintLanguage, setComplaintLanguage] = useState<"da" | "en">("da");
  const [complaintBody, setComplaintBody] = useState("");
  const [yearBookings, setYearBookings] = useState<
    Array<{
      key: string;
      channel: "villavilla" | "admiralstrand";
      bookingId: string;
      periodStart: string;
      periodEnd: string;
    }>
  >([]);

  useEffect(() => {
    if (!houseId) {
      setData(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    Promise.all([
      fetchJson<MeterReadingsResponse>(`/api/houses/${houseId}/meter-readings`),
      fetchJson<ConsumptionResponse>(`/api/houses/${houseId}/consumption`),
    ])
      .then(([meterReadingsResponse, consumptionResponse]) => {
        setData(meterReadingsResponse);
        setConsumptionData(consumptionResponse);
      })
      .catch((loadError) =>
        setError(
          loadError instanceof Error
            ? loadError.message
            : t("meterReadings.loadFailed", "Failed to load meter readings"),
        ),
      )
      .finally(() => setIsLoading(false));
  }, [houseId, t]);
  useEffect(() => {
    if (!houseId) {
      setYearBookings([]);
      return;
    }
    fetchJson<BookingResponse>(`/api/bookings?house=${houseId}&year=${selectedYear}`)
      .then((response) => {
        const today = new Date().toISOString().slice(0, 10);
        const rows = response.bookings
          .filter(
            (booking) =>
              booking.startDate <= today &&
              Boolean(booking.status) &&
              UNAVAILABLE_STATUSES.has(booking.status ?? "") &&
              !EXCLUDED_METER_READING_STATUSES.has(booking.status ?? ""),
          )
          .map((booking) => {
            const source =
              booking.sources.find(
                (entry) =>
                  (entry.channel === "villavilla" || entry.channel === "admiralstrand") &&
                  Boolean(entry.bookingId),
              ) ??
              booking.sources.find(
                (entry) => entry.channel === "villavilla" || entry.channel === "admiralstrand",
              );
            if (!source) {
              return null;
            }
            const bookingId = (source.bookingId ?? booking.id).trim();
            if (!bookingId) {
              return null;
            }
            const channel = source.channel;
            if (channel !== "villavilla" && channel !== "admiralstrand") {
              return null;
            }
            return {
              key: `${channel}|${bookingId}|${booking.startDate}|${booking.endDate}`,
              channel,
              bookingId,
              periodStart: booking.startDate,
              periodEnd: booking.endDate,
            };
          })
          .filter(
            (
              row,
            ): row is {
              key: string;
              channel: "villavilla" | "admiralstrand";
              bookingId: string;
              periodStart: string;
              periodEnd: string;
            } => row !== null,
          );
        setYearBookings(rows);
      })
      .catch(() => {
        setYearBookings([]);
      });
  }, [houseId, selectedYear]);
  const allowedBookingKeys = useMemo(() => new Set(yearBookings.map((row) => row.key)), [yearBookings]);
  const eloverblikByBookingFallback = useMemo(() => {
    const byDate = new Map((consumptionData?.eloverblikDaily ?? []).map((entry) => [entry.date, entry.value]));
    const map = new Map<string, number>();
    for (const booking of yearBookings) {
      let total = 0;
      for (
        let current = parseUtcDate(booking.periodStart);
        current < parseUtcDate(booking.periodEnd);
        current = addDays(current, 1)
      ) {
        total += byDate.get(formatDate(current)) ?? 0;
      }
      map.set(booking.key, total);
    }
    return map;
  }, [consumptionData, yearBookings]);

  if (!houseId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("meterReadings.title", "Meter readings")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">
            {t("meterReadings.selectHouse", "Select a house to review meter readings.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const traces = useMemo(
    () =>
      (data?.automaticTrace ?? [])
        .filter((row) => row.utilityType === activeTab)
        .sort((left, right) => {
          if (left.periodStart !== right.periodStart) {
            return left.periodStart.localeCompare(right.periodStart);
          }
          return left.bookingId.localeCompare(right.bookingId);
        }),
    [activeTab, data],
  );
  const bookingRows = useMemo(() => {
    const eloverblikByBooking = new Map<string, number>();
    const comparisonByKey = new Map<
      string,
      { paidForKwh: number; utilityRevenueDkk: number; eloverblikKwh: number }
    >();
    const comparisonRowsByBookingIdentity = new Map<
      string,
      Array<{
        periodStart: string;
        periodEnd: string;
        paidForKwh: number;
        utilityRevenueDkk: number;
        eloverblikKwh: number;
      }>
    >();
    for (const row of consumptionData?.comparison.rows ?? []) {
      const key = `${row.channel}|${row.bookingId}|${row.periodStart}|${row.periodEnd}`;
      eloverblikByBooking.set(key, row.eloverblikKwh);
      comparisonByKey.set(key, {
        paidForKwh: row.paidForKwh,
        utilityRevenueDkk: row.utilityRevenueDkk,
        eloverblikKwh: row.eloverblikKwh,
      });
      const identityKey = `${row.channel}|${row.bookingId}`;
      const existingRows = comparisonRowsByBookingIdentity.get(identityKey) ?? [];
      existingRows.push({
        periodStart: row.periodStart,
        periodEnd: row.periodEnd,
        paidForKwh: row.paidForKwh,
        utilityRevenueDkk: row.utilityRevenueDkk,
        eloverblikKwh: row.eloverblikKwh,
      });
      comparisonRowsByBookingIdentity.set(identityKey, existingRows);
    }
    const grouped = new Map<
      string,
      {
        key: string;
        channel: string;
        bookingId: string;
        periodStart: string;
        periodEnd: string;
        unit: string;
        paidDkk: number;
        usage: number;
        reportedUsage: number | null;
        lowestReportedStart: number | null;
        reportedEndForLowestStart: number | null;
        rawRanges: Array<{ text: string; impliedUsage: number }>;
        eloverblikKwh: number | null;
      }
    >();
    for (const row of traces) {
      const key = `${row.channel}|${row.bookingId}|${row.periodStart}|${row.periodEnd}`;
      if (allowedBookingKeys.size > 0 && !allowedBookingKeys.has(key)) {
        continue;
      }
      const existing = grouped.get(key);
      const rangeText = `${row.rawStartText || row.parsedStartValue}-${row.rawEndText || row.parsedEndValue}`;
      const impliedUsage = Math.max(0, row.parsedEndValue - row.parsedStartValue);
      if (!existing) {
        grouped.set(key, {
          key,
          channel: row.channel,
          bookingId: row.bookingId,
          periodStart: row.periodStart,
          periodEnd: row.periodEnd,
          unit: row.unit,
          paidDkk: row.utilityRevenueDkk,
          usage: row.derivedUsage,
          reportedUsage: impliedUsage,
          lowestReportedStart: row.parsedStartValue,
          reportedEndForLowestStart: row.parsedEndValue,
          rawRanges: [{ text: rangeText, impliedUsage }],
          eloverblikKwh:
            row.utilityType === "electricity"
              ? (eloverblikByBooking.get(key) ?? eloverblikByBookingFallback.get(key) ?? null)
              : null,
        });
        continue;
      }
      existing.paidDkk += row.utilityRevenueDkk;
      existing.usage += row.derivedUsage;
      existing.reportedUsage =
        existing.reportedUsage === null ? impliedUsage : existing.reportedUsage + impliedUsage;
      if (existing.lowestReportedStart === null || row.parsedStartValue < existing.lowestReportedStart) {
        existing.lowestReportedStart = row.parsedStartValue;
        existing.reportedEndForLowestStart = row.parsedEndValue;
      }
      if (!existing.rawRanges.some((entry) => entry.text === rangeText)) {
        existing.rawRanges.push({ text: rangeText, impliedUsage });
      }
    }
    for (const booking of yearBookings) {
      if (!grouped.has(booking.key)) {
        const comparisonRow = comparisonByKey.get(booking.key);
        const fallbackComparisonRow =
          comparisonRow ??
          comparisonRowsByBookingIdentity
            .get(`${booking.channel}|${booking.bookingId}`)
            ?.find(
              (row) => row.periodStart < booking.periodEnd && row.periodEnd > booking.periodStart,
            );
        const fallbackUsage =
          activeTab === "electricity" ? (fallbackComparisonRow?.paidForKwh ?? 0) : 0;
        const fallbackPaidDkk =
          activeTab === "electricity" ? (fallbackComparisonRow?.utilityRevenueDkk ?? 0) : 0;
        grouped.set(booking.key, {
          key: booking.key,
          channel: booking.channel,
          bookingId: booking.bookingId,
          periodStart: booking.periodStart,
          periodEnd: booking.periodEnd,
          unit: activeTab === "electricity" ? "kWh" : "m3",
          paidDkk: fallbackPaidDkk,
          usage: fallbackUsage,
          reportedUsage: null,
          lowestReportedStart: null,
          reportedEndForLowestStart: null,
          rawRanges: [],
          eloverblikKwh:
            activeTab === "electricity"
              ? (fallbackComparisonRow?.eloverblikKwh ??
                eloverblikByBooking.get(booking.key) ??
                eloverblikByBookingFallback.get(booking.key) ??
                null)
              : null,
        });
      }
    }
    return Array.from(grouped.values()).sort((left, right) => {
      if (left.periodStart !== right.periodStart) {
        return left.periodStart.localeCompare(right.periodStart);
      }
      return left.bookingId.localeCompare(right.bookingId);
    });
  }, [activeTab, allowedBookingKeys, consumptionData, eloverblikByBookingFallback, traces, yearBookings]);
  const overviewRows = useMemo(() => {
    const rows = new Map<
      string,
      {
        key: string;
        channel: "villavilla" | "admiralstrand";
        bookingId: string;
        periodStart: string;
        periodEnd: string;
        paidForUsage: number | null;
        comparisonUsage: number | null;
        deviationPercent: number | null;
        deviationDkk: number | null;
      }
    >();
    if (activeTab === "electricity") {
      for (const row of consumptionData?.comparison.rows ?? []) {
        const key = `${row.channel}|${row.bookingId}|${row.periodStart}|${row.periodEnd}`;
        if (allowedBookingKeys.size > 0 && !allowedBookingKeys.has(key)) {
          continue;
        }
        const hasPaidForSettlement = row.paidForKwh > 0 || row.utilityRevenueDkk > 0;
        rows.set(key, {
          key,
          channel: row.channel,
          bookingId: row.bookingId,
          periodStart: row.periodStart,
          periodEnd: row.periodEnd,
          paidForUsage: hasPaidForSettlement ? row.paidForKwh : null,
          comparisonUsage: row.eloverblikKwh,
          deviationPercent: hasPaidForSettlement ? row.deviationPercent : null,
          deviationDkk: hasPaidForSettlement ? row.deviationDkk : null,
        });
      }
    } else {
      for (const row of bookingRows) {
        const channel = row.channel === "villavilla" || row.channel === "admiralstrand" ? row.channel : null;
        if (!channel) {
          continue;
        }
        rows.set(row.key, {
          key: row.key,
          channel,
          bookingId: row.bookingId,
          periodStart: row.periodStart,
          periodEnd: row.periodEnd,
          paidForUsage: row.usage,
          comparisonUsage: row.reportedUsage,
          deviationPercent: null,
          deviationDkk: null,
        });
      }
    }
    for (const booking of yearBookings) {
      if (!rows.has(booking.key)) {
        rows.set(booking.key, {
          key: booking.key,
          channel: booking.channel,
          bookingId: booking.bookingId,
          periodStart: booking.periodStart,
          periodEnd: booking.periodEnd,
          paidForUsage: null,
          comparisonUsage:
            activeTab === "electricity"
              ? (eloverblikByBookingFallback.get(booking.key) ?? null)
              : null,
          deviationPercent: null,
          deviationDkk: null,
        });
      }
    }
    return Array.from(rows.values()).sort((left, right) => {
      if (left.periodStart !== right.periodStart) {
        return left.periodStart.localeCompare(right.periodStart);
      }
      return left.bookingId.localeCompare(right.bookingId);
    });
  }, [activeTab, allowedBookingKeys, bookingRows, consumptionData, yearBookings]);
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    const extractYear = (value: string) => Number.parseInt(value.slice(0, 4), 10);
    overviewRows.forEach((row) => {
      const year = extractYear(row.periodStart);
      if (Number.isFinite(year)) {
        years.add(year);
      }
    });
    bookingRows.forEach((row) => {
      const year = extractYear(row.periodStart);
      if (Number.isFinite(year)) {
        years.add(year);
      }
    });
    (consumptionData?.inBetweenPeriods ?? []).forEach((row) => {
      const year = extractYear(row.periodStart);
      if (Number.isFinite(year)) {
        years.add(year);
      }
    });
    return Array.from(years).sort((left, right) => right - left);
  }, [bookingRows, consumptionData, overviewRows]);
  useEffect(() => {
    if (availableYears.length === 0) {
      return;
    }
    if (!availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0] ?? selectedYear);
    }
  }, [availableYears, selectedYear]);
  const filteredOverviewRows = useMemo(
    () => {
      const today = new Date().toISOString().slice(0, 10);
      return overviewRows.filter(
        (row) =>
          Number.parseInt(row.periodStart.slice(0, 4), 10) === selectedYear && row.periodStart <= today,
      );
    },
    [overviewRows, selectedYear],
  );
  const filteredBookingRows = useMemo(
    () => {
      const today = new Date().toISOString().slice(0, 10);
      return bookingRows.filter(
        (row) =>
          Number.parseInt(row.periodStart.slice(0, 4), 10) === selectedYear && row.periodStart <= today,
      );
    },
    [bookingRows, selectedYear],
  );
  const inBetweenRows = useMemo(
    () => {
      const today = new Date().toISOString().slice(0, 10);
      return (consumptionData?.inBetweenPeriods ?? []).filter(
        (row) =>
          Number.parseInt(row.periodStart.slice(0, 4), 10) === selectedYear && row.periodStart <= today,
      );
    },
    [consumptionData, selectedYear],
  );
  const filteredInBetweenRows = useMemo(
    () => inBetweenRows.filter((row) => row.utilityType === activeTab),
    [activeTab, inBetweenRows],
  );
  const paidElectricityRows = useMemo(
    () =>
      (consumptionData?.comparison.rows ?? [])
        .filter((row) => row.paidForKwh > 0 || row.utilityRevenueDkk > 0)
        .sort((left, right) => {
          if (left.periodStart !== right.periodStart) {
            return left.periodStart.localeCompare(right.periodStart);
          }
          return left.bookingId.localeCompare(right.bookingId);
        }),
    [consumptionData],
  );
  const paidElectricityDateRange = useMemo(() => {
    if (paidElectricityRows.length === 0) {
      return { minStart: "", maxEnd: "" };
    }
    return paidElectricityRows.reduce(
      (accumulator, row) => ({
        minStart:
          accumulator.minStart.length === 0 || row.periodStart < accumulator.minStart
            ? row.periodStart
            : accumulator.minStart,
        maxEnd:
          accumulator.maxEnd.length === 0 || row.periodEnd > accumulator.maxEnd
            ? row.periodEnd
            : accumulator.maxEnd,
      }),
      { minStart: "", maxEnd: "" },
    );
  }, [paidElectricityRows]);
  useEffect(() => {
    if (paidElectricityDateRange.minStart.length === 0 || paidElectricityDateRange.maxEnd.length === 0) {
      return;
    }
    setComplaintStartDate((current) => (current.length === 0 ? paidElectricityDateRange.minStart : current));
    setComplaintEndDate((current) => (current.length === 0 ? paidElectricityDateRange.maxEnd : current));
  }, [paidElectricityDateRange.maxEnd, paidElectricityDateRange.minStart]);
  const complaintRows = useMemo(() => {
    return paidElectricityRows
      .filter((row) => row.channel === complaintChannel)
      .filter((row) => {
        if (complaintStartDate.length === 0 || complaintEndDate.length === 0) {
          return true;
        }
        return row.periodStart <= complaintEndDate && row.periodEnd >= complaintStartDate;
      })
      .map((row) => ({
        ...row,
        underpaidDkk: Math.max(0, -(row.deviationDkk ?? 0)),
      }))
      .filter((row) => row.underpaidDkk > complaintThresholdDkk);
  }, [complaintChannel, complaintEndDate, complaintStartDate, complaintThresholdDkk, paidElectricityRows]);
  const complaintTotalUnderpaidDkk = useMemo(
    () => complaintRows.reduce((sum, row) => sum + row.underpaidDkk, 0),
    [complaintRows],
  );
  const complaintChannelHouseId = useMemo(() => {
    const channelHouseId = consumptionData?.channelHouseIds?.[complaintChannel] ?? "";
    const normalized = channelHouseId.trim();
    return normalized.length > 0 ? normalized : null;
  }, [complaintChannel, consumptionData]);
  const generatedComplaintEmail = useMemo(() => {
    const numberFormatter = new Intl.NumberFormat(complaintLanguage === "da" ? "da-DK" : "en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const formatValue = (value: number) => numberFormatter.format(value);
    const headerColumns = [
      complaintLanguage === "da" ? "booking ID" : "booking ID",
      complaintLanguage === "da" ? "periode" : "period",
      complaintLanguage === "da" ? "afregnet kWh" : "settled kWh",
      complaintLanguage === "da" ? "ELOverblik kWh" : "ELOverblik kWh",
      ...(includeCleaningReadingColumn
        ? [complaintLanguage === "da" ? "rengøring kWh" : "rengøring kWh"]
        : []),
      complaintLanguage === "da" ? "betalt for lidt DKK" : "underpaid DKK",
    ];
    const rowColumns = complaintRows.map((row) => {
      const baseColumns = [
        row.bookingId,
        `${row.periodStart} - ${row.periodEnd}`,
        formatValue(row.paidForKwh),
        formatValue(row.eloverblikKwh),
      ];
      if (includeCleaningReadingColumn) {
        baseColumns.push("");
      }
      baseColumns.push(formatValue(row.underpaidDkk));
      return baseColumns;
    });
    const allRows = [headerColumns, ...rowColumns];
    const columnCount = headerColumns.length;
    const columnWidths = Array.from({ length: columnCount }, (_, index) =>
      allRows.reduce((maxWidth, row) => Math.max(maxWidth, (row[index] ?? "").length), 0),
    );
    const border = `+${columnWidths.map((width) => "-".repeat(width + 2)).join("+")}+`;
    const renderRow = (row: string[]) =>
      `| ${row.map((cell, index) => (cell ?? "").padEnd(columnWidths[index] ?? 0)).join(" | ")} |`;
    const listLines =
      rowColumns.length > 0
        ? [border, renderRow(headerColumns), border, ...rowColumns.map(renderRow), border].join("\n")
        : complaintLanguage === "da"
          ? "Ingen rækker fundet."
          : "No rows found.";
    if (complaintLanguage === "da") {
      return [
        complaintChannel === "admiralstrand" ? "Hej Admiral Strand," : "Hej VillaVilla,",
        "",
        `Jeg har ved gennemgang af forbrugsafregningen for mit hus${
          complaintChannelHouseId ? ` (ID ${complaintChannelHouseId})` : ""
        } opdaget en række fejl i afregningen for EL hvor jeg er blevet betalt for lidt.`,
        "Jeg ved ikke hvad der er gået galt, men det forbrug der er blevet afregnet ud fra stemmer ikke med det som servicefirmaet har indberettet.",
        "Og det stemmer heller ikke med det faktiske forbrug som jeg kan se på eloverblik.dk.",
        ...(includeCleaningReadingColumn ? ["Aflæsningsbilag fra rengøringsfirmaet er vedlagt.", ""] : []),
        "",
        "Her er oversigten over bookinger hvor I har betalt for lidt:",
        listLines,
        "",
        `Total tilgodehavende som I bedes overføre snarest: ${formatValue(complaintTotalUnderpaidDkk)} DKK`,
        "",
        "Med venlig hilsen",
      ].join("\n");
    }
    return [
      complaintChannel === "admiralstrand" ? "Hello Admiral Strand," : "Hello VillaVilla,",
      "",
      `During my review of the utility settlement for my house${
        complaintChannelHouseId ? ` (ID ${complaintChannelHouseId})` : ""
      }, I found several errors in the electricity settlement where I have been underpaid.`,
      "I do not know what went wrong, but the settled usage does not match what the service company reported.",
      "It also does not match the actual usage shown on eloverblik.dk.",
      ...(includeCleaningReadingColumn ? ["Vouchers from cleaners are attached.", ""] : []),
      "",
      "Here is the overview of bookings where you have paid too little:",
      listLines,
      "",
      `Total receivable that I ask you to transfer as soon as possible: ${formatValue(complaintTotalUnderpaidDkk)} DKK`,
      "",
      "Kind regards",
    ].join("\n");
  }, [
    complaintChannel,
    complaintChannelHouseId,
    complaintLanguage,
    complaintRows,
    complaintTotalUnderpaidDkk,
    includeCleaningReadingColumn,
  ]);
  useEffect(() => {
    setComplaintBody(generatedComplaintEmail);
  }, [generatedComplaintEmail]);
  const showEloverblikColumns = activeTab === "electricity" && Boolean(consumptionData?.comparison.hasEloverblik);
  const isCompactOverviewLayout = activeTab === "water" || !showEloverblikColumns;
  const bookingIdColumnClass = isCompactOverviewLayout
    ? "py-1 pr-3 w-[140px] whitespace-nowrap"
    : "py-1 pr-3 w-[110px] whitespace-nowrap";
  const channelColumnClass = isCompactOverviewLayout
    ? "py-1 pr-3 w-[90px] whitespace-nowrap"
    : "py-1 pr-3 w-[80px] whitespace-nowrap";
  const periodColumnClass = isCompactOverviewLayout
    ? "py-1 pr-3 w-[220px] whitespace-nowrap"
    : "py-1 pr-3 w-[170px] whitespace-nowrap";
  const numericColumnClass = isCompactOverviewLayout ? "py-1 pr-3 w-[120px] whitespace-nowrap" : "py-1 pr-3";
  const overviewTotals = useMemo(() => {
    const rows = filteredOverviewRows;
    const paidForUsageTotal = rows.reduce((sum, row) => sum + (row.paidForUsage ?? 0), 0);
    const comparisonUsageTotal = rows.reduce((sum, row) => sum + (row.comparisonUsage ?? 0), 0);
    const hasComparisonUsage = rows.some((row) => row.comparisonUsage !== null);
    const deviationDkkTotal = rows.reduce((sum, row) => sum + (row.deviationDkk ?? 0), 0);
    const deviationPercentValues = rows
      .map((row) => row.deviationPercent)
      .filter((value): value is number => value !== null);
    const deviationPercentAverage =
      deviationPercentValues.length > 0
        ? deviationPercentValues.reduce((sum, value) => sum + value, 0) / deviationPercentValues.length
        : null;
    return {
      paidForUsageTotal,
      comparisonUsageTotal,
      hasComparisonUsage,
      deviationDkkTotal,
      deviationPercentAverage,
    };
  }, [filteredOverviewRows]);
  const inBetweenTotals = useMemo(() => {
    const rows = filteredInBetweenRows;
    return {
      days: rows.reduce((sum, row) => sum + row.days, 0),
      usage: rows.reduce((sum, row) => sum + row.usage, 0),
      usagePerDayAverage:
        rows.length > 0 ? rows.reduce((sum, row) => sum + row.usagePerDay, 0) / rows.length : 0,
      costDkk: rows.reduce((sum, row) => sum + row.costDkk, 0),
      costPerDayAverage:
        rows.length > 0
          ? rows.reduce((sum, row) => sum + row.costPerDayDkk, 0) / rows.length
          : 0,
    };
  }, [filteredInBetweenRows]);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-lg font-semibold text-ink">
          {t("meterReadings.title", "Automatic utility reading reconstruction")}
        </p>
        <p className="text-sm text-muted">
          {t(
            "meterReadings.subtitle",
            "Paid-for derived consumption is primary, and parsed readings are shown as source evidence.",
          )}
        </p>
      </div>
      {isLoading ? <p className="text-sm text-muted">{t("meterReadings.loading", "Loading...")}</p> : null}
      {error ? <p className="text-sm text-error">{error}</p> : null}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <UtilityTypeTabs
          activeType={activeTab}
          onChange={setActiveTab}
          electricityLabel={t("meterReadings.electricity", "Electricity")}
          waterLabel={t("meterReadings.water", "Water")}
        />
        <div className="flex items-end gap-3">
          {availableYears.length > 0 ? (
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-ink" htmlFor="meter-readings-year">
                {t("common.year", "Year")}
              </label>
              <select
                id="meter-readings-year"
                className="h-9 rounded-md border px-3 text-sm brand-input brand-focus"
                value={String(selectedYear)}
                onChange={(event) => setSelectedYear(Number.parseInt(event.target.value, 10))}
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="outline"
            title={t("meterReadings.complaintTooltip", "Write complaint to agency")}
            aria-label={t("meterReadings.complaintTooltip", "Write complaint to agency")}
            onClick={() => setIsComplaintModalOpen(true)}
            disabled={activeTab !== "electricity"}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className="mr-2 h-4 w-4">
              <path
                d="M4 6.75A2.75 2.75 0 0 1 6.75 4h10.5A2.75 2.75 0 0 1 20 6.75v10.5A2.75 2.75 0 0 1 17.25 20H6.75A2.75 2.75 0 0 1 4 17.25V6.75Zm2.3-.25 5.7 4.56 5.7-4.56a1.25 1.25 0 0 0-.45-.08H6.75c-.16 0-.31.03-.45.08Zm11.2 1.8-4.72 3.77a1.25 1.25 0 0 1-1.56 0L6.5 8.3v8.95c0 .14.11.25.25.25h10.5c.14 0 .25-.11.25-.25V8.3Z"
                fillRule="evenodd"
                clipRule="evenodd"
                fill="currentColor"
              />
            </svg>
            <span>{t("meterReadings.complaintButtonTitle", "Write complaint")}</span>
          </Button>
        </div>
      </div>

      {(activeTab === "electricity" || activeTab === "water") ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("meterReadings.overviewTitle", "Booking consumption overview")}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b text-left text-muted">
                  <th className={bookingIdColumnClass}>
                    {t("consumption.booking", "Booking")}
                  </th>
                  <th className={channelColumnClass}>{t("common.channel", "Channel")}</th>
                  <th className={periodColumnClass}>
                    {t("consumption.period", "Period")}
                  </th>
                  <th className={numericColumnClass}>
                    {t("consumption.paidForDerived", "Paid for")} ({activeTab === "electricity" ? "kWh" : "m3"})
                  </th>
                  <th className={numericColumnClass}>
                    {activeTab === "electricity"
                      ? showEloverblikColumns
                        ? t("consumption.eloverblikKwh", "ElOverblik (kWh)")
                        : t("meterReadings.reportedUsageKwh", "Reported (kWh)")
                      : t("meterReadings.reportedUsageM3", "Reported (m3)")}
                  </th>
                  {showEloverblikColumns ? (
                    <>
                      <th className="py-1 pr-3">{t("consumption.deviationPercent", "Deviation %")}</th>
                      <th className="py-1 pr-3">{t("consumption.deviationDkk", "Deviation DKK")}</th>
                    </>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {filteredOverviewRows.map((row) => {
                  const percentLabel = formatSignedPercent(row.deviationPercent);
                  const dkkLabel = formatSignedDkk(row.deviationDkk);
                  return (
                    <tr key={row.key} className="border-b">
                      <td className={bookingIdColumnClass}>
                        <a
                          href={`#${toBookingAnchorId(row.key)}`}
                          className="font-medium text-ink underline-offset-2 hover:underline brand-focus rounded-sm"
                        >
                          {row.bookingId || "—"}
                        </a>
                      </td>
                      <td className={channelColumnClass}>{channelLabel(row.channel)}</td>
                      <td className={periodColumnClass}>
                        {row.periodStart} → {row.periodEnd}
                      </td>
                      <td className={numericColumnClass}>
                        {row.paidForUsage === null ? "—" : row.paidForUsage.toFixed(2)}
                      </td>
                      <td className={numericColumnClass}>
                        {row.comparisonUsage === null ? "—" : row.comparisonUsage.toFixed(2)}
                      </td>
                      {showEloverblikColumns ? (
                        <>
                          <td className={`py-1 pr-3 ${deviationPercentClass(row.deviationPercent)}`}>
                            {percentLabel}
                          </td>
                          <td className={`py-1 pr-3 ${deviationDkkClass(row.deviationDkk)}`}>{dkkLabel}</td>
                        </>
                      ) : null}
                    </tr>
                  );
                })}
                {filteredOverviewRows.length === 0 ? (
                  <tr>
                    <td colSpan={showEloverblikColumns ? 7 : 5} className="py-2 text-xs text-muted">
                      {t("meterReadings.noOverviewRows", "No booking overview rows available.")}
                    </td>
                  </tr>
                ) : null}
              </tbody>
              {filteredOverviewRows.length > 0 ? (
                <tfoot>
                  <tr className="border-t font-semibold">
                    <td className={bookingIdColumnClass}>{t("meterReadings.total", "Total")}</td>
                    <td className={channelColumnClass}>—</td>
                    <td className={periodColumnClass}>—</td>
                    <td className={numericColumnClass}>{overviewTotals.paidForUsageTotal.toFixed(2)}</td>
                    <td className={numericColumnClass}>
                      {overviewTotals.hasComparisonUsage ? overviewTotals.comparisonUsageTotal.toFixed(2) : "—"}
                    </td>
                    {showEloverblikColumns ? (
                      <>
                        <td className="py-1 pr-3">
                          {overviewTotals.deviationPercentAverage === null
                            ? "—"
                            : `${overviewTotals.deviationPercentAverage >= 0 ? "+" : ""}${overviewTotals.deviationPercentAverage.toFixed(1)}%`}
                        </td>
                        <td className="py-1 pr-3">
                          {`${overviewTotals.deviationDkkTotal >= 0 ? "+" : ""}${overviewTotals.deviationDkkTotal.toFixed(2)}`}
                        </td>
                      </>
                    ) : null}
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </CardContent>
        </Card>
      ) : null}

      {(activeTab === "electricity" || activeTab === "water") ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("consumption.inBetweenTable", "In-between periods (non-booked)")}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b text-left text-muted">
                  <th className="py-1 pr-3 w-[170px] whitespace-nowrap">
                    {t("consumption.period", "Period")}
                  </th>
                  <th className="py-1 pr-3 whitespace-nowrap">
                    {t("meterReadings.days", "Days")}
                  </th>
                  <th className="py-1 pr-3 whitespace-nowrap">
                    {activeTab === "electricity"
                      ? showEloverblikColumns
                        ? t("consumption.eloverblikKwh", "ElOverblik (kWh)")
                        : `${t("consumption.electricity", "Electricity")} (kWh)`
                      : `${t("consumption.water", "Water")} (m3)`}
                  </th>
                  <th className="py-1 pr-3 whitespace-nowrap">
                    {t("consumption.usagePerDay", "Usage/day")}
                  </th>
                  <th className="py-1 pr-3 whitespace-nowrap">
                    {t("meterReadings.costDkk", "Cost (DKK)")}
                  </th>
                  <th className="py-1 pr-3 whitespace-nowrap">
                    {t("meterReadings.costPerDayDkk", "Cost/day (DKK)")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredInBetweenRows.map((row) => (
                  <tr key={`${row.periodStart}:${row.periodEnd}`} className="border-b">
                    <td className="py-1 pr-3 whitespace-nowrap">
                      {row.periodStart} → {row.periodEnd}
                    </td>
                    <td className="py-1 pr-3">{row.days}</td>
                    <td className="py-1 pr-3">{row.usage.toFixed(2)}</td>
                    <td className="py-1 pr-3">
                      {row.usagePerDay.toFixed(2)} {row.unit}/{t("meterReadings.dayShort", "day")}
                    </td>
                    <td className="py-1 pr-3">{row.costDkk.toFixed(2)}</td>
                    <td className="py-1 pr-3">{row.costPerDayDkk.toFixed(2)}</td>
                  </tr>
                ))}
                {filteredInBetweenRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-2 text-xs text-muted">
                      {t("consumption.noInBetweenRows", "No in-between periods found in selected range.")}
                    </td>
                  </tr>
                ) : null}
              </tbody>
              {filteredInBetweenRows.length > 0 ? (
                <tfoot>
                  <tr className="border-t font-semibold">
                    <td className="py-1 pr-3 whitespace-nowrap">{t("meterReadings.total", "Total")}</td>
                    <td className="py-1 pr-3">{inBetweenTotals.days}</td>
                    <td className="py-1 pr-3">{inBetweenTotals.usage.toFixed(2)}</td>
                    <td className="py-1 pr-3">
                      {inBetweenTotals.usagePerDayAverage.toFixed(2)}{" "}
                      {activeTab === "electricity" ? "kWh" : "m3"}/{t("meterReadings.dayShort", "day")}
                    </td>
                    <td className="py-1 pr-3">{inBetweenTotals.costDkk.toFixed(2)}</td>
                    <td className="py-1 pr-3">{inBetweenTotals.costPerDayAverage.toFixed(2)}</td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </CardContent>
        </Card>
      ) : null}

      <div className="space-y-3">
        {filteredBookingRows.map((row, index) => {
          const previous = index > 0 ? filteredBookingRows[index - 1] : null;
          const rowReadingStart = row.lowestReportedStart;
          const rowReadingEnd = rowReadingStart !== null ? rowReadingStart + row.usage : null;
          const endIsCalculated =
            rowReadingEnd !== null && row.reportedEndForLowestStart !== null
              ? Math.abs(rowReadingEnd - row.reportedEndForLowestStart) > 0.0001
              : false;
          const bookingDays = Math.max(
            1,
            Math.round(
              (parseUtcDate(row.periodEnd).getTime() - parseUtcDate(row.periodStart).getTime()) /
                (24 * 60 * 60 * 1000),
            ),
          );
          const derivedPerDay = row.usage / bookingDays;
          const previousReadingEnd =
            previous !== null && previous.lowestReportedStart !== null
              ? previous.lowestReportedStart + previous.usage
              : Number.NEGATIVE_INFINITY;
          const showBetweenWarning =
            Boolean(previous) && rowReadingStart !== null && rowReadingStart < previousReadingEnd;
          return (
            <React.Fragment key={row.key}>
              {showBetweenWarning ? (
                <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  {t("meterReadings.newMeterQuestion", "New meter installed?")}
                </p>
              ) : null}
              <div id={toBookingAnchorId(row.key)} className="scroll-mt-24">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {row.periodStart} → {row.periodEnd}
                    </CardTitle>
                    <p className="text-xs text-muted">
                      {row.channel} · {row.bookingId}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-ink">
                      {t("meterReadings.paidForUsage", "Paid {dkk} DKK for usage {usage} {unit}", {
                        dkk: formatDkkValue(row.paidDkk),
                        usage: formatUsageValue(row.usage),
                        unit: row.unit,
                      })}
                    </p>
                    <p className="text-xs text-muted">
                      {t("meterReadings.derivedUsagePerDay", "Derived usage per day: {value} {unit}", {
                        value: formatUsageValue(derivedPerDay),
                        unit: row.unit,
                      })}
                    </p>
                    {row.unit === "kWh" && showEloverblikColumns && row.eloverblikKwh !== null ? (
                      <p
                        className={
                          row.usage > 0 && row.eloverblikKwh > row.usage * 1.1
                            ? "text-xs text-red-700 font-medium"
                            : "text-xs text-muted"
                        }
                      >
                        {t("meterReadings.eloverblikUsage", "ElOverblik consumption: {value} kWh", {
                          value: formatUsageValue(row.eloverblikKwh),
                        })}{" "}
                        ({formatUsageValue(row.eloverblikKwh / bookingDays)} kWh/day)
                      </p>
                    ) : null}
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <p className="text-xs font-semibold text-muted">
                          {t("meterReadings.readingStart", "Reading start")}
                        </p>
                        <p className="text-sm text-ink">
                          {rowReadingStart === null ? "—" : formatMeterValue(rowReadingStart)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-muted">
                          {t("meterReadings.readingEnd", "Reading end")}
                        </p>
                        <p className="text-sm text-ink">
                          {rowReadingEnd === null ? "—" : formatMeterValue(rowReadingEnd)}
                          {rowReadingEnd !== null && endIsCalculated
                            ? ` ${t("meterReadings.calculatedSuffix", "(calculated)")}`
                            : ""}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-muted">
                        {channelLabel(
                          row.channel === "villavilla" || row.channel === "admiralstrand"
                            ? row.channel
                            : "villavilla",
                        )}{" "}
                        {t("meterReadings.reportedReadings", "reported readings")}
                      </p>
                      <div className="rounded-md border bg-slate-50 p-3 text-xs space-y-1">
                        {row.rawRanges.length > 0 ? (
                          row.rawRanges.map((entry) => {
                            const matchesDerived = Math.abs(entry.impliedUsage - row.usage) < 0.01;
                            return (
                              <p
                                key={entry.text}
                                className={matchesDerived ? "text-emerald-700 font-medium" : "text-ink"}
                              >
                                {entry.text} ({formatUsageValue(entry.impliedUsage)} {row.unit})
                              </p>
                            );
                          })
                        ) : (
                          <p className="text-ink">—</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </React.Fragment>
          );
        })}
        {filteredBookingRows.length === 0 ? (
          <p className="text-sm text-muted">{t("meterReadings.noPairs", "No readings yet.")}</p>
        ) : null}
      </div>

      {isComplaintModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <Card className="w-full max-w-6xl">
            <CardHeader className="relative">
              <CardTitle>{t("meterReadings.complaintModalTitle", "Complaint email draft")}</CardTitle>
              <button
                type="button"
                className="absolute right-4 top-4 text-xl text-muted transition hover:text-ink"
                onClick={() => setIsComplaintModalOpen(false)}
                aria-label={t("common.close", "Close")}
              >
                ×
              </button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                <label className="space-y-1">
                  <span className="text-sm font-medium text-ink">
                    {t("meterReadings.complaintChannelLabel", "Channel")}
                  </span>
                  <select
                    className="h-9 w-full rounded-md border px-3 text-sm brand-input brand-focus"
                    value={complaintChannel}
                    onChange={(event) =>
                      setComplaintChannel(event.target.value as "villavilla" | "admiralstrand")
                    }
                  >
                    <option value="admiralstrand">{channelLabel("admiralstrand")}</option>
                    <option value="villavilla">{channelLabel("villavilla")}</option>
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-sm font-medium text-ink">
                    {t("meterReadings.complaintStartDateLabel", "Start date")}
                  </span>
                  <input
                    type="date"
                    className="h-9 w-full rounded-md border px-3 text-sm brand-input brand-focus"
                    value={complaintStartDate}
                    onChange={(event) => setComplaintStartDate(event.target.value)}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-sm font-medium text-ink">
                    {t("meterReadings.complaintEndDateLabel", "End date")}
                  </span>
                  <input
                    type="date"
                    className="h-9 w-full rounded-md border px-3 text-sm brand-input brand-focus"
                    value={complaintEndDate}
                    onChange={(event) => setComplaintEndDate(event.target.value)}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-sm font-medium text-ink">
                    {t("meterReadings.complaintThresholdLabel", "Deviation threshold (DKK)")}
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="h-9 w-full rounded-md border px-3 text-sm brand-input brand-focus"
                    value={String(complaintThresholdDkk)}
                    onChange={(event) => {
                      const nextValue = Number.parseFloat(event.target.value);
                      setComplaintThresholdDkk(Number.isFinite(nextValue) ? Math.max(0, nextValue) : 0);
                    }}
                  />
                </label>
                <div className="space-y-1">
                  <span className="text-sm font-medium text-ink">
                    {t("meterReadings.complaintLanguageLabel", "Language")}
                  </span>
                  <div role="radiogroup" className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={complaintLanguage === "da" ? "default" : "outline"}
                      aria-checked={complaintLanguage === "da"}
                      role="radio"
                      onClick={() => setComplaintLanguage("da")}
                    >
                      {t("meterReadings.complaintLanguageDanish", "Danish")}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={complaintLanguage === "en" ? "default" : "outline"}
                      aria-checked={complaintLanguage === "en"}
                      role="radio"
                      onClick={() => setComplaintLanguage("en")}
                    >
                      {t("meterReadings.complaintLanguageEnglish", "English")}
                    </Button>
                  </div>
                </div>
                <label className="flex items-center gap-2 pt-7 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={includeCleaningReadingColumn}
                    onChange={(event) => setIncludeCleaningReadingColumn(event.target.checked)}
                  />
                  {t(
                    "meterReadings.complaintIncludeCleaningReading",
                    'Include "rengøring kWh" column.',
                  )}
                </label>
              </div>
              <p className="text-xs text-muted">
                {t(
                  "meterReadings.complaintSummary",
                  "{count} bookings, total underpayment {amount} DKK",
                  {
                    count: String(complaintRows.length),
                    amount: complaintTotalUnderpaidDkk.toFixed(2),
                  },
                )}
              </p>
              <textarea
                className="min-h-[520px] w-full rounded-md border px-3 py-2 text-xs font-mono brand-input brand-focus"
                value={complaintBody}
                onChange={(event) => setComplaintBody(event.target.value)}
              />
              <div className="flex justify-end">
                <Button type="button" variant="ghost" onClick={() => setIsComplaintModalOpen(false)}>
                  {t("common.close", "Close")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
};
